// src/lib/quickMessages.ts -- Phase 7: Quick Message client helpers.
//
// Four API endpoints + shared types + the time-since label rule from
// 31_quick_message/02_gotchas.md §11.
//
// BIGINT trap: `id` and `replyToId` are strings on the wire. We keep them
// as strings throughout the client to stay clear of Number.MAX_SAFE_INTEGER
// trouble and to mirror the API.
//
// QM blue (`#3D6FA8` family) is for Quick Message only -- per gotcha #9
// the literal hex codes live in QM components, not the theme tokens.

import { apiFetch } from "../api";

// ---------------------------------------------------------------------------
// Quick Message blue palette (hex literals, by design -- see gotcha #9).
// ---------------------------------------------------------------------------

export const QM = {
  base:    "#3D6FA8",
  dark:    "#2A5380",
  light:   "#A8C3DD",
  bg:      "#E2ECF6",
  glow:    "#E8F1FA",
  // Used for the qm-tint avatar gradient (prototype's `.avatar.qm-tint`).
  avatarA: "#6E97C6",
  avatarB: "#3D6FA8",
} as const;

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------

export interface QmSender {
  id: number | null;
  firstName: string;
  lastName: string;
  role: string | null;
  deleted: boolean;
}

export interface QuickMessage {
  id: string;
  sender: QmSender;
  body: string;
  createdAt: string;
  readAt: string | null;
  replyToId: string | null;
  threadCount: number;
}

export interface QuickMessageInbox {
  messages: QuickMessage[];
}

export interface ThreadMessage {
  id: string;
  direction: "sent" | "received";
  body: string;
  createdAt: string;
}

export interface ThreadResponse {
  messages: ThreadMessage[];
}

export interface SendResponse {
  id: string;
  createdAt: string;
}

export interface PatchResponse {
  id: string;
  readAt: string | null;
  dismissedAt: string | null;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

export function fetchInbox(): Promise<QuickMessageInbox> {
  return apiFetch<QuickMessageInbox>("/api/me/quick-messages");
}

export function sendQuickMessage(body: {
  recipientId: number;
  body: string;
  replyToId?: string | null;
}): Promise<SendResponse> {
  return apiFetch<SendResponse>("/api/me/quick-messages", {
    method: "POST",
    body: JSON.stringify({
      recipientId: body.recipientId,
      body: body.body,
      replyToId: body.replyToId ?? null,
    }),
  });
}

export function patchQuickMessage(
  id: string,
  body: { readAt?: true; dismissedAt?: true },
): Promise<PatchResponse> {
  return apiFetch<PatchResponse>(`/api/me/quick-messages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function fetchThread(id: string): Promise<ThreadResponse> {
  return apiFetch<ThreadResponse>(`/api/me/quick-messages/${id}/thread`);
}

// ---------------------------------------------------------------------------
// Time-since label (gotcha #11)
// ---------------------------------------------------------------------------

/** Render-ready uppercase label for the banner eyebrow's "QUICK MESSAGE · {…}" suffix. */
export function timeSinceLabel(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "JUST NOW";

  const elapsedMs = now.getTime() - t;
  // Clamp future drift (server clock ahead of client) -- treat as "just now".
  if (elapsedMs < 0) return "JUST NOW";

  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) return "JUST NOW";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} MIN AGO`;

  const hours = Math.floor(minutes / 60);
  if (hours < 3) return `${hours} HR AGO`;

  const then = new Date(t);
  if (sameCalendarDay(then, now)) {
    const h = then.getHours();
    if (h < 12) return "THIS MORNING";
    if (h < 17) return "THIS AFTERNOON";
    return "THIS EVENING";
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameCalendarDay(then, yesterday)) {
    return `YESTERDAY · ${formatHM(then)}`;
  }

  const sixDaysAgoStart = startOfDay(now);
  sixDaysAgoStart.setDate(sixDaysAgoStart.getDate() - 6);
  if (then.getTime() >= sixDaysAgoStart.getTime()) {
    const day = then
      .toLocaleDateString("en-GB", { weekday: "short" })
      .toUpperCase();
    return `${day} ${formatHM(then)}`;
  }

  const dm = then
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
    .toUpperCase();
  return `${dm} ${formatHM(then)}`;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function formatHM(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

// ---------------------------------------------------------------------------
// History strip eyebrow ("EARLIER · TODAY" / "THIS WEEK" / "LAST WEEK")
// ---------------------------------------------------------------------------

/** Suffix for the thread-strip eyebrow, based on the oldest prior message. */
export function historyEyebrowSuffix(
  threadMessages: ReadonlyArray<ThreadMessage>,
  now: Date = new Date(),
): string {
  if (threadMessages.length === 0) return "TODAY";
  const oldest = new Date(threadMessages[0].createdAt);
  if (sameCalendarDay(oldest, now)) return "TODAY";

  const weekStart = startOfDay(now);
  weekStart.setDate(weekStart.getDate() - 6);
  if (oldest.getTime() >= weekStart.getTime()) return "THIS WEEK";

  const lastWeekStart = startOfDay(now);
  lastWeekStart.setDate(lastWeekStart.getDate() - 13);
  if (oldest.getTime() >= lastWeekStart.getTime()) return "LAST WEEK";
  return "EARLIER";
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** First-letter monogram for the avatar (handles the orphan-sender case). */
export function senderInitials(sender: QmSender): string {
  if (sender.deleted) return "?";
  const a = (sender.firstName ?? "").trim()[0] ?? "";
  const b = (sender.lastName ?? "").trim()[0] ?? "";
  return (a + b) || "?";
}

/** "Margaret W." -- short label used on name chips in the multi-pill state. */
export function senderShortName(sender: QmSender): string {
  if (sender.deleted) return "Former colleague";
  const first = (sender.firstName ?? "").trim();
  const lastInitial = (sender.lastName ?? "").trim()[0];
  if (!first) return "Unknown";
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

/** "Margaret Wright" -- full label for banners. */
export function senderFullName(sender: QmSender): string {
  if (sender.deleted) return "Former colleague";
  return `${sender.firstName ?? ""} ${sender.lastName ?? ""}`.trim() || "Unknown";
}

/** "DUTY MANAGER · 09:38" -- the expanded banner's secondary meta line. */
export function bannerMetaLine(message: QuickMessage): string {
  const role = (message.sender.role ?? "").trim().toUpperCase();
  const hm = formatHM(new Date(message.createdAt));
  return role ? `${role} · ${hm}` : hm;
}
